import { Injectable } from '@angular/core';
import { AutoCompleteService, ConfiguracaoEntidadeObservable, EntityService, HttpClient, KeycloakAuthHttp, monthNames, NumberUtils, PageParameters } from '@elotech/angular-common';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Competencia } from '../../../competencia/competencia';
import { moment } from '../history-slider/history-slider.component';
import { CompetenciaDTO } from "../../../lancamento/model";


@Injectable()
export class CompetenciaService extends EntityService<any> implements AutoCompleteService {

  constructor(
    public httpClient: HttpClient,
    private _keycloak: KeycloakAuthHttp,
    private _configuracaoEntidade: ConfiguracaoEntidadeObservable
  ) {
    super(httpClient);
    this.resource = '/api/competencias';
  }

  public findOne(id: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/recuperar`,
      id,
      fields: this.fields.toString(),
      location: this.location,
      disableSpinner: true
    });
  }

  findAllCompetenciasNaoBloqueadas(ano: number, entidadeId: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/exercicio/${ano}/entidade/${entidadeId}/nao-bloqueadas`,
      disableSpinner: true,
      location: this.location
    });
  }

  findToOptions(searchValue: string): Observable<any[]> {
    return this.httpClient.get({
      resource: `${this.resource}/all`,
      page: new PageParameters(15, 0, 'exercicio.ano,desc&sort=mes,desc'),
      search: this.getSearch(searchValue),
      disableSpinner: true,
      fields: this.fields.toString(),
      location: this.location
    }).pipe(
      map(page => page.content)
    );
  }

  getCompetenciasVizinhas(competencia: any): Observable<any> {
    if (!competencia) {
      return of(null);
    }

    const requestParam = {
      resource: `${this.resource}/vizinhas`,
      disableSpinner: true,
      body: competencia
    };
    return this.httpClient.post(requestParam);
  }

  objToOption(competencia: any): string {
    return `${competencia.numeroMes} - ${competencia.descricaoMes} / ${competencia.ano} (${competencia.situacaoDescricao})`;
  }

  getSearch(searchValue) {
    return this.montarFiltro(searchValue);
  }

  getCompetenciaPadrao(disableSpinner = false): Observable<any> {

    if (!this._keycloak.getUsuarioInfo()) {
      return of();
    }

    const { exercicioPadrao, mesPadrao } = this._keycloak.getUsuarioInfo();

    if (exercicioPadrao && mesPadrao) {
      return this.findCompetencia(mesPadrao, exercicioPadrao, disableSpinner);
    }

    return of();
  }

  getCompetenciaEsocialPadrao(disableSpinner = false): Observable<any> {

    if (!this._keycloak.getUsuarioInfo()) {
      return of();
    }

    const { exercicioPadrao, mesPadrao } = this._keycloak.getUsuarioInfo();

    if (exercicioPadrao && mesPadrao) {
      return this.findEsocialCompetencia(mesPadrao, exercicioPadrao, disableSpinner);
    }

    return of();
  }

  getinicioCompetencia() {
    const { exercicioPadrao, mesPadrao } = this._keycloak.getUsuarioInfo();
    return moment(exercicioPadrao + '-' + mesPadrao + '-01').format('YYYY-MM-DD');
  }

  getFimCompetencia() {
    const { exercicioPadrao, mesPadrao } = this._keycloak.getUsuarioInfo();
    return moment(exercicioPadrao + '-' + mesPadrao + '-01').endOf('month').format('YYYY-MM-DD');
  }

  existePosterioresFechadas(data: any) {
    return this.httpClient.get({
      resource: `${this.resource}/existe-posteriores-fechadas?data=${data}`,
      disableSpinner: true
    });
  }

  isFechada(data: any) {
    return this.httpClient.get({
      resource: `${this.resource}/is-fechada?data=${data}`,
      disableSpinner: true
    });
  }

  isPeriodoEmCompetenciaFechada(dataInicial: any, dataFinal?: any, ateLogada: boolean = false) {

    let params = `dataInicial=${dataInicial}`;

    dataFinal ? params = params.concat(`&dataFinal=${dataFinal}`) : '';
    ateLogada ? params = params.concat('&ateLogada=true') : '';

    return this.httpClient.get({
      resource: `${this.resource}/periodo-competencia-fechada?${params}`,
      disableSpinner: true
    });
  }

  findCompetencia(mes: number, ano: number, disableSpinner = false): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/recuperar/${mes}/${ano}`,
      fields: this.fields.toString(),
      location: this.location,
      disableSpinner: disableSpinner
    });
  }

  findEsocialCompetencia(mes: number, ano: number, disableSpinner = false): Observable<Competencia> {
    return this.httpClient.get({
      resource: `${this.resource}/recuperar-esocial/${mes}/${ano}`,
      fields: this.fields.toString(),
      location: this.location,
      disableSpinner
    });
  }

  findCompetenciaEsocialById(id: number, disableSpinner = false): Observable<Competencia> {
    return this.httpClient.get({
      resource: `${this.resource}/esocial`,
      id,
      fields: this.fields.toString(),
      location: this.location,
      disableSpinner
    });
  }

  findCompetencias(searchValue: string, showCompenteciaFechada = false, onlyCompetenciasFuturas = false, showCompetenciaBloqueada = false): Observable<any[]> {
    return this._findCompetenciasByEndPoint(searchValue, 'all', showCompenteciaFechada, onlyCompetenciasFuturas, showCompetenciaBloqueada);
  }

  findCompetenciasEsocial(searchValue: string, showCompenteciaFechada = false, onlyCompetenciasFuturas = false): Observable<any[]> {
    return this._findCompetenciasByEndPoint(searchValue, 'with-esocial', showCompenteciaFechada, onlyCompetenciasFuturas);
  }

  findCompetenciasByMesAno(mes: number, ano: number): Observable<any[]> {
    if (isNaN(mes) || isNaN(ano)) {
      throw `invalid argument(mes=${mes},ano=${ano})`;
    }
    let search = `exercicio.ano == ${Number(ano)};mes == ${Number(mes)}`;

    return this.httpClient.get({
      resource: `${this.resource}/all`,
      page: new PageParameters(15, 0, 'exercicio.ano,desc&sort=mes,desc'),
      search: search,
      disableSpinner: true,
      fields: this.fields.toString(),
      location: this.location
    }).pipe(
      map(page => page.content[0])
    );
  }

  reabrirCompetencia(id: number) {
    return this.httpClient.put({
      resource: `${this.resource}/${id}`,
      disableSpinner: true
    });
  }

  adicionarNovaCompetencia() {
    return this.httpClient.post({
      resource: `${this.resource}/new/competencia`,
      disableSpinner: true
    });
  }

  fecharCompetencia(id: number) {
    return this.httpClient.put({
      resource: `${this.resource}/fechar/${id}`,
      disableSpinner: false
    });
  }

  public updateCompetencias(competencia: any) {
    if (competencia && competencia.id && competencia.ano === this._keycloak.getUsuarioInfo().exercicioPadrao) {
      this._configuracaoEntidade.competencias = this.findAllCompetenciasNaoBloqueadas(competencia.ano, this._keycloak.getUsuarioInfo().entidadePadrao.id);
    }
  }

  public getNextCompetenciaAberta(data): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/proxima-competencia-aberta/${data}`,
      disableSpinner: true
    });
  }

  public agendarPrevisaoPagamento(id: number, body: any): Observable<void> {
    return this.httpClient.post({
      resource: `${this.resource}/previsao-pagamento`,
      id: id,
      body: body,
      disableSpinner: true
    });
  }

  public async getPrevisaoPagamento(competencia: number, exercicio: number): Promise<any> {
    return await this.httpClient.get({
      resource: `${this.resource}/encontrar-previsao-de-pagamento/${competencia}/${exercicio}`,
      disableSpinner: true
    }).toPromise();
  }

  getCompetenciaLogadaOuPadrao(competenciaId: number): Observable<any> {
    if (competenciaId) {
      return this.findOne(competenciaId);
    }

    return this.getCompetenciaPadrao();
  }

  findCompetenciasPeriodo(mesInicio: number, anoInicio: number, mesFim: number, anoFim: number, disableSpinner: boolean): Observable<CompetenciaDTO[]> {
    return this.httpClient.get({
      resource: `${this.resource}/competencias-periodo/${mesInicio}/${anoInicio}/${mesFim}/${anoFim}`,
      disableSpinner: disableSpinner
    });
  }

  private montarFiltro(searchValue) {

    if (!searchValue) {
      return '';
    }

    const splitted: Array<any> = searchValue.trim().split(new RegExp(/\/| /));

    let filter = '';
    let juncao = '';

    if (splitted && splitted.length > 0) {

      splitted.forEach(item => {
        if (item) {
          if (NumberUtils.isNumeric(item)) {
            if (item >= 1 && item <= 12) {
              filter += juncao + `mes == ${item}`;
              juncao = ' and ';
            }

            if (`${item}`.length === 4) {
              filter += juncao + `exercicio.ano == ${item}`;
              juncao = ' and ';
            }
          } else {
            let abreParenteses = '(';
            let usouFiltro = false;

            monthNames
              .filter(mes => mes.toUpperCase().startsWith(item.toUpperCase()))
              .map(item => {
                return (monthNames.lastIndexOf(item) + 1);
              })
              .forEach(mes => {
                filter += juncao + abreParenteses + `mes == ${mes}`;
                juncao = ' or ';
                abreParenteses = '';
                usouFiltro = true;
              });

            if (usouFiltro) {
              filter += ')';
              juncao = ' and ';
            }
          }
        }
      });
    }

    return filter;
  }

  private _findCompetenciasByEndPoint(searchValue: string,
                                      endPoint: string,
                                      showCompenteciaFechada = false,
                                      onlyCompetenciasFuturas = false,
                                      showCompenteciaBloqueada = false): Observable<any[]> {

    let search = this.getSearch(searchValue);

    let sort = 'exercicio.ano,desc&sort=mes,desc';

    const { exercicioPadrao, mesPadrao } = this._keycloak.getUsuarioInfo();

    let searchSituacao = 'situacao == "A"';

    if (onlyCompetenciasFuturas) {
      search = `${search ? search + ';' : ''} ((mes>=${mesPadrao};exercicio.ano>=${exercicioPadrao}),(exercicio.ano>${exercicioPadrao}))`;
    }

    if (showCompenteciaFechada) {
      searchSituacao = ' situacao != "B" ';
    }

    if (showCompenteciaBloqueada) {
      searchSituacao = 'situacao != "F"';
      sort = 'exercicio.ano,asc&sort=mes,asc'
    }

    search = `${search ? search + ';' : ''} ${searchSituacao}`;

    return this.httpClient.get({
      resource: `${this.resource}/${endPoint}`,
      page: new PageParameters(15, 0, sort),
      search,
      disableSpinner: true,
      fields: this.fields.toString(),
      location: this.location
    }).pipe(
      map(page => page.content)
    );
  }
}
